import React, { useState, useRef } from 'react';
import { useUserVideos, useDeleteVideo, useUploadVideo } from '../../hooks/unified/useVideoQueries';
import { useQueryClient } from '@tanstack/react-query';
import { LoadingSpinner } from '../ui/LoadingSpinner';
import { useToast } from '../../contexts/ToastContext';
import { YouTubeVideoPicker } from './YouTubeVideoPicker';
import { unifiedApiService } from '../../services/unified';

// Feature flag - set to true when direct upload is ready
const ENABLE_DIRECT_UPLOAD = false;

export const VideoManagementCard: React.FC = () => {
  const { data: videos, isLoading, refetch } = useUserVideos();
  const deleteVideoMutation = useDeleteVideo();
  const uploadVideoMutation = useUploadVideo();
  const queryClient = useQueryClient();
  const { showSuccess, showError } = useToast();

  const MAX_VIDEOS = 2;
  const videoCount = videos?.length || 0;
  const isLimitReached = videoCount >= MAX_VIDEOS;

  const [uploadProgress, setUploadProgress] = useState(0);
  const [isUploading, setIsUploading] = useState(false);
  const [isLinking, setIsLinking] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [deleteConfirmId, setDeleteConfirmId] = useState<string | null>(null);
  const [showYouTubePicker, setShowYouTubePicker] = useState(false);

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  const formatDuration = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      handleUpload(file);
    }
  };

  const handleUpload = async (file: File) => {
    if (isLimitReached) {
      showError(
        'Video Limit Reached',
        `You can upload a maximum of ${MAX_VIDEOS} videos. Please delete an existing video before uploading a new one.`
      );
      return;
    }

    // Validate file type
    if (!file.type.startsWith('video/')) {
      showError('Invalid File Type', 'Only video files are accepted.');
      return;
    }

    // Validate file size (100MB)
    const maxSize = 100 * 1024 * 1024;
    if (file.size > maxSize) {
      showError('File Too Large', 'Video size must be less than 100MB.');
      return;
    }

    setIsUploading(true);
    setUploadProgress(0);

    try {
      await uploadVideoMutation.mutateAsync({
        file,
        onProgress: (progress) => {
          setUploadProgress(progress);
        },
      });

      showSuccess('Video Uploaded', 'Your video has been uploaded successfully.');
      setUploadProgress(0);
    } catch (err: any) {
      showError('Upload Failed', err.response?.data?.error || 'Failed to upload video. Please try again.');
    } finally {
      setIsUploading(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const handleUploadClick = () => {
    fileInputRef.current?.click();
  };

  const handleDeleteClick = (videoId: string) => {
    setDeleteConfirmId(videoId);
  };

  const handleDeleteConfirm = async () => {
    if (!deleteConfirmId) return;

    try {
      await deleteVideoMutation.mutateAsync(deleteConfirmId);
      showSuccess('Video Deleted', 'Your video has been deleted successfully.');
    } catch (err: any) {
      const errorMsg = err.response?.data?.error || err.message || 'Failed to delete video';
      showError('Delete Failed', errorMsg);
    } finally {
      setDeleteConfirmId(null);
    }
  };

  const handleDeleteCancel = () => {
    setDeleteConfirmId(null);
  };

  const handleYouTubeVideoSelect = async (youtubeVideo: any) => {
    setIsLinking(true);
    try {
      await unifiedApiService.candidates.linkYouTubeVideo({
        youtube_video_id: youtubeVideo.id,
        title: youtubeVideo.title,
        description: youtubeVideo.description,
      });
      
      setShowYouTubePicker(false);
      showSuccess('YouTube Video Linked', 'Your YouTube video has been linked successfully.');
      
      // Refresh the videos list immediately
      await queryClient.invalidateQueries({ queryKey: ['videos', 'user'] });
      refetch();
    } catch (error: any) {
      console.error('Failed to link YouTube video:', error);
      const errorMessage = error.message || error.response?.data?.error || 'Failed to link YouTube video';
      
      // Provide more specific error messages
      if (errorMessage.includes('limit') || errorMessage.includes('maximum')) {
        showError('Video Limit Reached', `You can only have ${MAX_VIDEOS} videos. Please delete one first.`);
      } else if (errorMessage.includes('already linked') || errorMessage.includes('duplicate')) {
        showError('Already Linked', 'This YouTube video is already linked to your profile.');
      } else {
        showError('Link Failed', errorMessage);
      }
    } finally {
      setIsLinking(false);
    }
  };

  const renderVideoCard = (video: any) => {
    const isYouTubeVideo = video.video_source === 'youtube';
    
    return (
      <div
        key={video.id}
        className="p-3 sm:p-4 border border-gray-200 rounded-lg hover:border-purple-300 transition-colors"
      >
        <div className="flex items-start gap-3 sm:gap-4">
          <div className="flex-shrink-0">
            {isYouTubeVideo && video.youtube_thumbnail_url ? (
              <div className="relative w-16 h-12 sm:w-24 sm:h-16 rounded-lg overflow-hidden">
                <img
                  src={video.youtube_thumbnail_url}
                  alt={video.youtube_title}
                  className="w-full h-full object-cover"
                />
                <div className="absolute inset-0 flex items-center justify-center bg-black bg-opacity-30 hover:bg-opacity-50 transition-opacity">
                  <svg className="w-6 h-6 sm:w-8 sm:h-8 text-white" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M8 5v14l11-7z"/>
                  </svg>
                </div>
              </div>
            ) : (
              <div className="w-12 h-12 sm:w-16 sm:h-16 bg-purple-100 rounded-lg flex items-center justify-center">
                <svg className="w-6 h-6 sm:w-8 sm:h-8 text-purple-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14.752 11.168l-3.197-2.132A1 1 0 0010 9.87v4.263a1 1 0 001.555.832l3.197-2.132a1 1 0 000-1.664z" />
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
            )}
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex flex-wrap items-center gap-1 sm:gap-2">
              <p className="text-sm font-medium text-gray-900 truncate max-w-[150px] sm:max-w-none">
                {isYouTubeVideo ? video.youtube_title : video.filename}
              </p>
              {isYouTubeVideo && (
                <span className="inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium bg-red-100 text-red-800">
                  <svg className="w-3 h-3 mr-0.5" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z"/>
                  </svg>
                  YT
                </span>
              )}
            </div>
            <p className="text-xs sm:text-sm text-gray-500 mt-0.5">
              {formatDuration(video.duration_seconds)} • {formatDate(video.uploaded_at)}
            </p>
            {isYouTubeVideo && video.youtube_video_id && (
              <a
                href={`https://www.youtube.com/watch?v=${video.youtube_video_id}`}
                target="_blank"
                rel="noopener noreferrer"
                className="text-xs text-purple-600 hover:text-purple-800 inline-flex items-center mt-1"
              >
                View on YouTube
                <svg className="w-3 h-3 ml-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                </svg>
              </a>
            )}
          </div>
          <button
            onClick={() => handleDeleteClick(video.id)}
            className="flex-shrink-0 p-2 text-red-600 hover:bg-red-50 rounded-md transition-colors"
            title={isYouTubeVideo ? 'Unlink YouTube video' : 'Delete video'}
          >
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
            </svg>
          </button>
        </div>
      </div>
    );
  };

  return (
    <div className="bg-white shadow rounded-lg p-4 sm:p-6 overflow-hidden">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-4">
        <div className="min-w-0">
          <h2 className="text-lg font-semibold text-gray-900">Video Management</h2>
          <p className="text-sm text-gray-600 mt-1">
            {videoCount} of {MAX_VIDEOS} videos uploaded
            {isLimitReached && (
              <span className="block sm:inline sm:ml-2 text-amber-600 font-medium">
                (Limit reached - delete a video to upload new one)
              </span>
            )}
          </p>
        </div>

        <div className="flex flex-wrap gap-2">
          <input
            ref={fileInputRef}
            type="file"
            accept="video/*"
            onChange={handleFileSelect}
            className="hidden"
            disabled={!ENABLE_DIRECT_UPLOAD}
          />
          <div className="relative group">
            <button
              onClick={handleUploadClick}
              disabled={!ENABLE_DIRECT_UPLOAD || isUploading || isLimitReached}
              className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-purple-600 hover:bg-purple-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-purple-500 disabled:opacity-50 disabled:cursor-not-allowed"
              title={!ENABLE_DIRECT_UPLOAD ? 'Coming soon - Use YouTube for now' : isLimitReached ? `Maximum ${MAX_VIDEOS} videos allowed` : 'Upload a new video'}
            >
              {isUploading ? (
                <>
                  <LoadingSpinner size="sm" className="mr-2" />
                  Uploading... {uploadProgress}%
                </>
              ) : (
                <>
                  <svg className="mr-2 h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12" />
                  </svg>
                  Upload
                </>
              )}
            </button>
            {!ENABLE_DIRECT_UPLOAD && (
              <span className="absolute -top-8 left-1/2 -translate-x-1/2 bg-gray-800 text-white text-xs px-2 py-1 rounded opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap">
                Coming soon
              </span>
            )}
          </div>
          <button
            onClick={() => setShowYouTubePicker(true)}
            disabled={isLimitReached || isLinking}
            className="inline-flex items-center px-4 py-2 border border-red-200 text-sm font-medium rounded-md shadow-sm text-red-700 bg-red-50 hover:bg-red-100 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 disabled:opacity-50 disabled:cursor-not-allowed"
            title={isLimitReached ? `Maximum ${MAX_VIDEOS} videos allowed` : 'Link from YouTube'}
          >
            {isLinking ? (
              <>
                <LoadingSpinner size="sm" className="mr-2" />
                Linking...
              </>
            ) : (
              <>
                <svg className="mr-2 h-4 w-4" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z"/>
                </svg>
                YouTube
              </>
            )}
          </button>
        </div>
      </div>

      {isLoading ? (
        <div className="flex justify-center py-8">
          <LoadingSpinner />
        </div>
      ) : videos && videos.length > 0 ? (
        <div className="space-y-3">
          {videos.map(renderVideoCard)}
        </div>
      ) : (
        <div className="py-8">
          {/* Empty state with instructions */}
          <div className="text-center mb-8">
            <svg className="mx-auto h-12 w-12 text-red-400" fill="currentColor" viewBox="0 0 24 24">
              <path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z"/>
            </svg>
            <h3 className="mt-3 text-lg font-medium text-gray-900">Add Your Video Introduction</h3>
            <p className="mt-1 text-sm text-gray-500">
              Link a video from your YouTube channel to showcase yourself to employers
            </p>
          </div>

          {/* Step-by-step instructions */}
          <div className="bg-gray-50 rounded-lg p-6 max-w-2xl mx-auto">
            <h4 className="text-sm font-semibold text-gray-900 mb-4 flex items-center">
              <span className="bg-red-100 text-red-700 rounded-full w-6 h-6 flex items-center justify-center text-xs mr-2">?</span>
              How to add a YouTube video
            </h4>
            <ol className="space-y-3 text-sm text-gray-600">
              <li className="flex items-start">
                <span className="bg-purple-100 text-purple-700 rounded-full w-5 h-5 flex items-center justify-center text-xs mr-3 mt-0.5 flex-shrink-0">1</span>
                <span>Click the <strong className="text-red-600">YouTube</strong> button above</span>
              </li>
              <li className="flex items-start">
                <span className="bg-purple-100 text-purple-700 rounded-full w-5 h-5 flex items-center justify-center text-xs mr-3 mt-0.5 flex-shrink-0">2</span>
                <span>Authorize access to your YouTube account (first time only)</span>
              </li>
              <li className="flex items-start">
                <span className="bg-purple-100 text-purple-700 rounded-full w-5 h-5 flex items-center justify-center text-xs mr-3 mt-0.5 flex-shrink-0">3</span>
                <span>Select a video from your channel to link to your profile</span>
              </li>
              <li className="flex items-start">
                <span className="bg-purple-100 text-purple-700 rounded-full w-5 h-5 flex items-center justify-center text-xs mr-3 mt-0.5 flex-shrink-0">4</span>
                <span>Your video will appear here and be visible to employers</span>
              </li>
            </ol>
            <div className="mt-4 pt-4 border-t border-gray-200">
              <p className="text-xs text-gray-500">
                💡 <strong>Tip:</strong> Record a 1-2 minute introduction video highlighting your skills and experience. 
                Upload it to YouTube (can be unlisted), then link it here.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* YouTube Video Picker Modal */}
      {showYouTubePicker && (
        <YouTubeVideoPicker
          onSelect={handleYouTubeVideoSelect}
          onClose={() => setShowYouTubePicker(false)}
        />
      )}

      {/* Delete Confirmation Modal */}
      {deleteConfirmId && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
            <h3 className="text-lg font-medium text-gray-900 mb-4">Delete Video?</h3>
            <p className="text-sm text-gray-600 mb-6">
              Are you sure you want to delete this video? This action cannot be undone.
            </p>
            <div className="flex justify-end space-x-3">
              <button
                onClick={handleDeleteCancel}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={handleDeleteConfirm}
                className="px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-md hover:bg-red-700"
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
